/**
 * Beheer — de instellingen van de werkplek (ontwerp v4 §8, plak 2.9).
 *
 * Zes losse admin-schermen komen hier bij elkaar: de instellingen van
 * het fundament (vlag, plafonds, wekkertermijnen), de regels voor
 * automatisch handelen, de postbussen, gebruikers en toegang, de
 * handtekeningen en de koppelingen.
 *
 * Twee dingen die hier boven alles gaan:
 *
 * 1. **Elke schakelaar schrijft direct weg.** De les van 8 september:
 *    een schakelaar die alleen het concept in de browser wijzigt, laat
 *    een klantregel live staan.
 * 2. **Van een koppeling staat alleen de naam in beeld.** Nooit een
 *    waarde, ook niet afgekort. Staat er "wacht op sleutel", dan noemt
 *    het scherm de naam van de instelling die nog leeg is — meer niet.
 *
 * Gebruikers en toegang bestaat al als eigen scherm sinds ronde 1;
 * hier staat een knop ernaartoe en geen tweede uitvoering.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { vertaalApiFout } from "@/lib/api";
import {
  werkplekApi,
  werkplekBeheerApi,
  type BeheerKoppeling,
  type BeheerRegel,
} from "@/lib/werkplek-api";
import { MetCopiloot } from "./Copiloot/MetCopiloot";
import {
  LEVERANCIER_ADRESSEN,
  LeverancierAdressen,
} from "./LeverancierAdressen";

interface Regel {
  sleutel: string;
  label: string;
  uitleg: string;
  soort: "schakelaar" | "getal" | "bedrag" | "klantnummers" | "accounts";
  /** Waarschuwing die bij het aanzetten wordt getoond. */
  waarschuwing?: string;
}

const REGELS: Regel[] = [
  {
    sleutel: "copiloot_alleen_afwijkingen",
    label: "Copiloot toont alleen afwijkingen (uit = altijd de volledige lijst)",
    uitleg:
      "Aan (standaard): bovenaan de copiloot staan alleen de afwijkingen, " +
      "in een rood blok 'Niet gevonden' of 'Twijfel', en de regel 'Wat is " +
      "gecontroleerd (N)', dicht; die klapt uit naar de volledige lijst. " +
      "Uit: altijd de volledige lijst, open. Geldt voor iedereen die de " +
      "werkplek gebruikt.",
    soort: "schakelaar",
  },
  {
    sleutel: "crediteren_echt",
    label: "Crediteren echt doorvoeren",
    uitleg:
      "Uit: de geldketen draait droog. Er gaat geen geld; het plan met de " +
      "exacte aanroepen wordt vastgelegd. Aan: na jouw akkoord maakt het portaal " +
      "de creditnota in Lightspeed, boekt hetzelfde bedrag terug via MultiSafepay " +
      "en mailt de klant. Kan de keten iets niet zelf (bijvoorbeeld een aftrek " +
      "van retourkosten), dan komt er een taak met de exacte klik.",
    soort: "schakelaar",
    waarschuwing:
      "Dit raakt geld. Zet hem alleen aan als je weet wat er daarna gebeurt.",
  },
  {
    sleutel: "ai_maandplafond_eur",
    label: "AI-plafond per maand",
    uitleg: "Het plafond dat in de kop van de copiloot staat, in euro.",
    soort: "bedrag",
  },
  {
    sleutel: "ai_dagplafond_eur",
    label: "AI-plafond per dag",
    uitleg: "Blijft naast het maandplafond bestaan, in euro.",
    soort: "bedrag",
  },
  {
    sleutel: "nasturen_grens_eur",
    label: "Nasturen zonder tussenkomst tot",
    uitleg:
      "Staat op nul: elke nazending is een klik van JP. Hoger zetten betekent " +
      "dat het portaal onder dat bedrag zelf mag nasturen.",
    soort: "bedrag",
  },
  {
    sleutel: "wekker_onderweg_werkdagen",
    label: "Wekker 'onderweg'",
    uitleg: "In werkdagen; weekend en feestdagen tellen niet mee.",
    soort: "getal",
  },
  {
    sleutel: "wekker_wacht_op_klant_werkdagen",
    label: "Wekker 'wacht op klant'",
    uitleg: "In werkdagen.",
    soort: "getal",
  },
  {
    sleutel: "wekker_case_werkdagen",
    label: "Wekker 'case bij de vervoerder'",
    uitleg: "In werkdagen.",
    soort: "getal",
  },
  {
    sleutel: "wekker_klacht_werkdagen",
    label: "Klachttermijn",
    uitleg:
      "In werkdagen. Bij een klacht schrijft Sanne \"je hoort uiterlijk <datum> " +
      "van ons\"; op die dag komt de kaart terug.",
    soort: "getal",
  },
  // TAP-tickets 28-10: tickets bij Cenfil ophalen en een wekker als ze stilstaan.
  {
    sleutel: "tap_tickets_sync_minuten",
    label: "TAP-tickets ophalen elke",
    uitleg: "In minuten, minimaal 15. 60 is elk uur, 240 is vier keer per dag.",
    soort: "getal",
  },
  {
    sleutel: "tap_tickets_sync_van_uur",
    label: "TAP-tickets ophalen vanaf",
    uitleg: "Uur van de dag, Amsterdamse tijd.",
    soort: "getal",
  },
  {
    sleutel: "tap_tickets_sync_tot_uur",
    label: "TAP-tickets ophalen tot en met",
    uitleg: "Uur van de dag, Amsterdamse tijd.",
    soort: "getal",
  },
  {
    sleutel: "tap_tickets_wekker_werkdagen",
    label: "Wekker als een ticket bij Cenfil stilstaat",
    uitleg:
      "In werkdagen zonder nieuwe reactie van de vervoerder; dan komt de zaak " +
      "van die bestelling terug.",
    soort: "getal",
  },
  {
    sleutel: "ongedaan_venster_seconden",
    label: "Venster om ongedaan te maken",
    uitleg: "In seconden, na het versturen of uitvoeren van een actie.",
    soort: "getal",
  },
  {
    sleutel: "herkenning_uitzondering_klantnummers",
    label: "Herkennen ondanks eigen domein",
    uitleg:
      "Lightspeed-klantnummers die ingelogd in de shop wel herkend worden, ook " +
      "met een e-mailadres op het eigen domein. Alleen klantnummers, gescheiden " +
      "door een komma; nooit een heel domein.",
    soort: "klantnummers",
  },
  {
    sleutel: "proef_accounts",
    label: "Proefaccounts",
    uitleg:
      "E-mailadressen of Lightspeed-klantnummers van eigen testaccounts, gescheiden " +
      "door een komma. Een chat of mail daarvan maakt een zaak met label proef, die " +
      "meteen in Afgerond staat; nooit een belofte, taak, wekker of uitgaande mail.",
    soort: "accounts",
  },
];

/**
 * Sessie 55 blok 2 (besluit JP: "Alle waarden instelbaar in Beheer"): de
 * grenzen van het schadefilter en de retourmotor. Intern: Nova en Sanne
 * noemen deze getallen nooit aan een klant.
 */
const SCHADE_RETOUR_REGELS: Regel[] = [
  {
    sleutel: "schade_melding_dagen",
    label: "Schade melden binnen",
    uitleg:
      "In dagen na de bezorgdatum van de vervoerder. Een latere melding " +
      "haalt deze controle niet.",
    soort: "getal",
  },
  {
    sleutel: "schade_venster_maanden",
    label: "Venster eerste melding",
    uitleg:
      "In maanden. Een tweede melding binnen dit venster is een herhaling: " +
      "dan altijd via JP en nooit een directe nazending. Telt ook voor de " +
      "herhalers op Gezondheid.",
    soort: "getal",
  },
  {
    sleutel: "schade_max_eur",
    label: "Schadewaarde tot",
    uitleg: "In euro. Een hogere schadewaarde haalt deze controle niet.",
    soort: "bedrag",
  },
  {
    sleutel: "schade_licht_procent",
    label: "Lichte schade: terug",
    uitleg: "Percentage van de productwaarde bij lichte schade.",
    soort: "getal",
  },
  {
    sleutel: "schade_zwaar_procent",
    label: "Zware schade: terug",
    uitleg:
      "Percentage van de productwaarde bij zware schade (of een nazending).",
    soort: "getal",
  },
  {
    sleutel: "retour_auto_grens_eur",
    label: "Retour zelf afronden tot",
    uitleg:
      "In euro. Tot dit bedrag rondt de motor een standaardretour na " +
      "ontvangst zelf af (alleen met de geldketen aan, nooit voor een " +
      "proefaccount); daarboven komt er een akkoord-kaart.",
    soort: "bedrag",
  },
  {
    sleutel: "retour_klant_termijn_dagen",
    label: "Retourtermijn in de klanttekst",
    uitleg:
      "In dagen. De termijn die in elke klanttekst staat; intern blijft er " +
      "ruimte daarboven.",
    soort: "getal",
  },
  {
    sleutel: "retour_verloop_scherp",
    label: "Retour zonder scan: herinneren en laten verlopen",
    uitleg:
      "Uit: de dagelijkse taak logt alleen wat hij zou doen. Aan: na 10 dagen " +
      "zonder eerste scan krijgt de klant een herinnering van Sanne, na 21 " +
      "dagen gaat de retour op verlopen met een mail. De termijnen staan in " +
      "de retourinstellingen.",
    soort: "schakelaar",
    waarschuwing: "Aan betekent: er gaan mails naar klanten.",
  },
  {
    sleutel: "account_uitnodiging_actief",
    label: "Accountuitnodiging na bezorging",
    uitleg:
      "Uit: na een betaalde order of herbestelling gaat direct de " +
      "wachtwoordmail van de webshop naar een klant met een onbevestigd " +
      "account. Aan: die directe mail vervalt; een werkdag na de bezorging " +
      "krijgt de klant de mail 'Bevestig je account bij VoerOnline', hooguit " +
      "eens per 30 dagen en drie keer per klant.",
    soort: "schakelaar",
    waarschuwing: "Aan betekent: er gaan mails naar klanten.",
  },
  {
    sleutel: "retour_label_max_pogingen",
    label: "Pogingen retourlabel",
    uitleg:
      "Hoe vaak de motor een label bij de vervoerder aanvraagt voor er een " +
      "kaart naar JP gaat.",
    soort: "getal",
  },
];

/**
 * Sessie 48 (G5): waar de terugsprong van Google landt. Het oude adres
 * bestond niet als scherm; hier staat de uitkomst boven de koppelingen.
 */
const GOOGLE_REDEN: Record<string, string> = {
  state_invalid: "de koppelsessie was verlopen of ongeldig. Start het koppelen opnieuw.",
  access_denied: "je gaf bij Google geen toestemming.",
  token_exchange_mislukt: "Google gaf geen sleutel terug. Probeer het opnieuw.",
};

export function GoogleUitkomst() {
  const [params, setParams] = useSearchParams();
  const stand = params.get("google");
  if (!stand) return null;
  const reden = params.get("reden") ?? "";
  const goed = stand === "verbonden";
  return (
    <div
      className="wp-blok"
      role={goed ? "status" : "alert"}
      data-testid="wp-beheer-google"
      style={{ borderColor: goed ? "var(--wp-groen)" : "var(--wp-rood)" }}
    >
      <p style={{ margin: 0 }}>
        {goed
          ? "Google is gekoppeld. Reviews en berichten van Google komen voortaan binnen."
          : `Google koppelen is niet gelukt: ${GOOGLE_REDEN[reden] ?? `Google meldde "${reden || "onbekend"}".`}`}
      </p>
      <button
        type="button"
        className="wp-knop"
        style={{ marginTop: 8 }}
        onClick={() => {
          params.delete("google");
          params.delete("reden");
          setParams(params, { replace: true });
        }}
      >
        Sluiten
      </button>
    </div>
  );
}

export function WerkplekBeheer() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["werkplek", "instellingen"],
    queryFn: werkplekApi.instellingen,
  });
  const stand = useQuery({
    queryKey: ["werkplek", "beheer", "stand"],
    queryFn: werkplekBeheerApi.stand,
  });

  const zetten = useMutation({
    mutationFn: ({ sleutel, waarde }: { sleutel: string; waarde: unknown }) =>
      werkplekApi.zetInstelling(sleutel, waarde),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["werkplek"] });
      toast.success("Opgeslagen.");
    },
    onError: (fout) => toast.error(vertaalApiFout(fout)),
  });

  return (
    <>
      <div className="wp-bovenbalk">
        <h1 style={{ fontSize: 17, margin: 0, fontWeight: 600 }}>Beheer</h1>
      </div>
      <div className="wp-inhoud">
        <MetCopiloot pagina="beheer">
        {/* Gecentreerd, maximaal 900 px (JP, punt 42 van 22-09). Links
            tegen de rand met een half scherm leeg ernaast leest als een
            pagina die nog niet af is. */}
        <div
          style={{
            overflowY: "auto",
            padding: 18,
            maxWidth: 900,
            width: "100%",
            marginInline: "auto",
          }}
        >
          <GoogleUitkomst />
          {isLoading ? (
            <p style={{ color: "var(--wp-tekst2)" }}>Bezig met laden…</p>
          ) : isError ? (
            <p style={{ color: "var(--wp-rood)" }}>
              De instellingen zijn niet op te halen. Alleen een beheerder mag
              deze pagina zien.
            </p>
          ) : (
            <div className="wp-blok">
              <h4>Instellingen</h4>
              <div>
                {REGELS.map((regel) => (
                  <InstellingRegel
                    key={regel.sleutel}
                    regel={regel}
                    waarde={data?.waarden?.[regel.sleutel]}
                    bezig={zetten.isPending}
                    onZet={(waarde) =>
                      zetten.mutate({ sleutel: regel.sleutel, waarde })
                    }
                  />
                ))}
              </div>
            </div>
          )}

          {/* ── Sessie 55: schade, kwaliteit en retour ───────────── */}
          {isLoading || isError ? null : (
            <div className="wp-blok" data-testid="wp-beheer-schade-retour">
              <h4>Schade, kwaliteit en retour</h4>
              <div>
                {SCHADE_RETOUR_REGELS.map((regel) => (
                  <InstellingRegel
                    key={regel.sleutel}
                    regel={regel}
                    waarde={data?.waarden?.[regel.sleutel]}
                    bezig={zetten.isPending}
                    onZet={(waarde) =>
                      zetten.mutate({ sleutel: regel.sleutel, waarde })
                    }
                  />
                ))}
                <LeverancierAdressen
                  waarde={data?.waarden?.[LEVERANCIER_ADRESSEN]}
                  bezig={zetten.isPending}
                  onZet={(waarde) =>
                    zetten.mutate({ sleutel: LEVERANCIER_ADRESSEN, waarde })
                  }
                />
              </div>
            </div>
          )}

          {/* ── Regels voor automatisch handelen ──────────────── */}
          <div className="wp-blok">
            <h4>Regels voor automatisch handelen</h4>
            {stand.isLoading ? (
              <p style={{ padding: "12px 14px", color: "var(--wp-tekst2)" }}>
                Bezig met laden…
              </p>
            ) : stand.isError || !stand.data ? (
              <p style={{ padding: "12px 14px", color: "var(--wp-rood)" }}>
                De regels zijn niet op te halen
                {stand.isError ? `: ${vertaalApiFout(stand.error)}` : "."}
              </p>
            ) : (
              <div>
                {stand.data.regels.map((regel) => (
                  <AutomatischeRegel
                    key={regel.sleutel}
                    regel={regel}
                    bezig={zetten.isPending}
                    onZet={(waarde) =>
                      zetten.mutate({ sleutel: regel.sleutel, waarde })
                    }
                  />
                ))}
              </div>
            )}
          </div>

          {/* ── Postbussen ────────────────────────────────────── */}
          <div className="wp-blok">
            <h4>
              Postbussen
              {stand.data
                ? ` \u00b7 ${stand.data.postbussen_actief} actief`
                : ""}
            </h4>
            {stand.data ? (
              stand.data.postbussen.length > 0 ? (
                <div>
                  {stand.data.postbussen.map((postbus) => (
                    <Rij
                      key={postbus.id}
                      titel={postbus.email_adres}
                      uitleg={
                        `${postbus.naam}` +
                        (postbus.is_default ? " \u00b7 hoofdpostbus" : "") +
                        (postbus.heeft_koppeling
                          ? ""
                          : " \u00b7 nog niet gekoppeld aan Gmail")
                      }
                      chip={postbus.is_actief ? "aan" : "uit"}
                      chipKlasse={
                        postbus.is_actief
                          ? "wp-chip wp-chip-whatsapp"
                          : "wp-chip wp-chip-grijs"
                      }
                    />
                  ))}
                </div>
              ) : (
                <p style={{ padding: "12px 14px", color: "var(--wp-tekst2)" }}>
                  Er is nog geen postbus ingericht.
                </p>
              )
            ) : null}
          </div>

          {/* ── Gebruikers en toegang: bestaat al als eigen scherm ─ */}
          <div className="wp-blok">
            <h4>Gebruikers en toegang</h4>
            <div style={{ padding: "12px 14px" }}>
              <p style={{ margin: "0 0 8px", color: "var(--wp-tekst2)" }}>
                Wie mag meekijken, beantwoorden en beheren. Dat scherm staat
                los, zodat het ook vanuit je profielmenu bereikbaar is.
              </p>
              <Link className="wp-knop" to="/beheer/gebruikers">
                Gebruikers en toegang openen
              </Link>
            </div>
          </div>

          {/* ── Waarheidskaarten (sessie 61): staat op Kennis en leren ─ */}
          <div className="wp-blok">
            <h4>Waarheidskaarten</h4>
            <div style={{ padding: "12px 14px" }}>
              <p style={{ margin: "0 0 8px", color: "var(--wp-tekst2)" }}>
                Wat Nova en Sanne over voeding en gezondheid mogen zeggen.
                Goedkeuren, uitzetten en bewerken gebeurt op Kennis en leren.
              </p>
              <Link className="wp-knop" to="/kennis?tab=kaarten">
                Waarheidskaarten openen
              </Link>
            </div>
          </div>

          {/* ── Handtekeningen ────────────────────────────────── */}
          <div className="wp-blok">
            <h4>Handtekeningen</h4>
            {stand.data ? (
              stand.data.handtekeningen.length > 0 ? (
                <div>
                  {stand.data.handtekeningen.map((rij) => (
                    <Rij
                      key={rij.id}
                      titel={rij.naam}
                      uitleg={`${rij.functie} \u00b7 ${rij.email}`}
                      chip={rij.is_ai_persona ? "AI-persona" : "mens"}
                      chipKlasse={
                        rij.is_ai_persona
                          ? "wp-chip wp-chip-mail"
                          : "wp-chip wp-chip-grijs"
                      }
                    />
                  ))}
                </div>
              ) : (
                <p style={{ padding: "12px 14px", color: "var(--wp-tekst2)" }}>
                  Er is nog geen medewerker met een handtekening.
                </p>
              )
            ) : null}
          </div>

          {/* ── Koppelingen ───────────────────────────────────── */}
          <div className="wp-blok">
            <h4>
              Koppelingen
              {stand.data
                ? ` \u00b7 ${stand.data.koppelingen_aan} van ${stand.data.koppelingen.length} aan`
                : ""}
            </h4>
            {stand.data ? (
              <div>
                {stand.data.koppelingen.map((koppeling) => (
                  <KoppelingRij key={koppeling.sleutel} koppeling={koppeling} />
                ))}
              </div>
            ) : null}
            <p
              style={{
                margin: 0,
                padding: "10px 14px",
                fontSize: 12,
                color: "var(--wp-tekst3)",
                borderTop: "1px solid var(--wp-lijn2)",
              }}
            >
              Sleutels zet je op Render, niet hier. Dit scherm toont alleen
              de naam van een sleutel en of hij gevuld is, nooit de waarde.
            </p>
          </div>
        </div>
      </MetCopiloot>
      </div>
    </>
  );
}

/** Eén regel met een titel, uitleg en een chip rechts. */
function Rij({
  titel,
  uitleg,
  chip,
  chipKlasse,
}: {
  titel: string;
  uitleg: string;
  chip?: string;
  chipKlasse?: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        alignItems: "baseline",
        padding: "10px 14px",
        borderBottom: "1px solid var(--wp-lijn2)",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>{titel}</div>
        <div style={{ fontSize: 12, color: "var(--wp-tekst2)" }}>{uitleg}</div>
      </div>
      {chip ? <span className={chipKlasse}>{chip}</span> : null}
    </div>
  );
}

/** "vandaag 14:12", "gisteren 09:03" of "21 sep 14:12", Amsterdamse tijd. */
function laatstGezienTekst(iso: string): string {
  const moment = new Date(iso);
  const tijd = moment.toLocaleTimeString("nl-NL", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Amsterdam",
  });
  const dag = (d: Date) =>
    d.toLocaleDateString("nl-NL", { timeZone: "Europe/Amsterdam" });
  const nu = new Date();
  const gisteren = new Date(nu.getTime() - 86_400_000);
  if (dag(moment) === dag(nu)) return `vandaag ${tijd}`;
  if (dag(moment) === dag(gisteren)) return `gisteren ${tijd}`;
  const datum = moment.toLocaleDateString("nl-NL", {
    day: "numeric",
    month: "short",
    timeZone: "Europe/Amsterdam",
  });
  return `${datum} ${tijd}`;
}

function KoppelingRij({ koppeling }: { koppeling: BeheerKoppeling }) {
  // Sessie 46 (A12): "aan" betekent sleutel aanwezig én recent een
  // geslaagde actie. Anders "controleer", met de reden in woorden: een
  // kleur alleen zegt niets.
  const stand = koppeling.stand;
  const chip =
    stand === "aan"
      ? "aan"
      : stand === "controleer"
        ? "controleer"
        : "wacht op sleutel";
  const chipKlasse =
    stand === "aan"
      ? "wp-chip wp-chip-whatsapp"
      : stand === "controleer"
        ? "wp-chip wp-chip-risico"
        : "wp-chip wp-chip-wekker";
  const gezien = koppeling.laatst_gezien
    ? ` \u00b7 ${koppeling.laatst_gezien_wat ?? "laatst gezien"} ${laatstGezienTekst(koppeling.laatst_gezien)}`
    : "";
  return (
    <Rij
      titel={koppeling.naam}
      uitleg={
        koppeling.waarvoor +
        (koppeling.toelichting ? ` \u00b7 ${koppeling.toelichting}` : "") +
        gezien +
        (koppeling.controleer_reden
          ? ` \u00b7 controleer: ${koppeling.controleer_reden}`
          : "") +
        // Namen van lege sleutels, nooit waarden: zo weet je wat je op
        // Render moet zetten zonder dat er iets uitlekt.
        (stand === "wacht_op_sleutel"
          ? ` \u00b7 nog leeg: ${koppeling.ontbrekende_velden.join(", ")}`
          : "")
      }
      chip={chip}
      chipKlasse={chipKlasse}
    />
  );
}

function AutomatischeRegel({
  regel,
  bezig,
  onZet,
}: {
  regel: BeheerRegel;
  bezig: boolean;
  onZet: (waarde: boolean) => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        gap: 16,
        alignItems: "flex-start",
        padding: "12px 14px",
        borderBottom: "1px solid var(--wp-lijn2)",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>{regel.label}</div>
        <div style={{ fontSize: 12, color: "var(--wp-tekst2)" }}>
          {regel.uitleg}
        </div>
        {/* Uitgeschakeld is uitleggen waarom, nooit stil verbergen. */}
        {regel.reden_uit ? (
          <div style={{ fontSize: 12, color: "var(--wp-rood)", marginTop: 4 }}>
            {regel.reden_uit}
          </div>
        ) : null}
      </div>
      <button
        type="button"
        className="wp-knop"
        disabled={bezig || !regel.bestaat}
        aria-pressed={regel.waarde === true}
        onClick={() => onZet(regel.waarde !== true)}
        style={
          regel.waarde === true
            ? {
                background: "var(--wp-groen)",
                borderColor: "var(--wp-groen)",
                color: "#fff",
              }
            : undefined
        }
      >
        {!regel.bestaat
          ? "Nog niet te zetten"
          : regel.waarde === true
            ? "Staat aan"
            : "Staat uit"}
      </button>
    </div>
  );
}

function InstellingRegel({
  regel,
  waarde,
  bezig,
  onZet,
}: {
  regel: Regel;
  waarde: unknown;
  bezig: boolean;
  onZet: (waarde: unknown) => void;
}) {
  const [concept, setConcept] = useState<string>(
    Array.isArray(waarde) ? waarde.join(", ") : String(waarde ?? ""),
  );

  return (
    <div
      style={{
        display: "flex",
        gap: 16,
        alignItems: "flex-start",
        padding: "12px 14px",
        borderBottom: "1px solid var(--wp-lijn2)",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>{regel.label}</div>
        <div style={{ fontSize: 12, color: "var(--wp-tekst2)" }}>
          {regel.uitleg}
        </div>
        {regel.waarschuwing && waarde !== true ? (
          <div style={{ fontSize: 12, color: "var(--wp-rood)", marginTop: 4 }}>
            {regel.waarschuwing}
          </div>
        ) : null}
      </div>

      {regel.soort === "schakelaar" ? (
        <button
          type="button"
          className="wp-knop"
          disabled={bezig}
          aria-pressed={waarde === true}
          onClick={() => onZet(waarde !== true)}
          style={
            waarde === true
              ? {
                  background: "var(--wp-groen)",
                  borderColor: "var(--wp-groen)",
                  color: "#fff",
                }
              : undefined
          }
        >
          {waarde === true ? "Staat aan" : "Staat uit"}
        </button>
      ) : (
        <form
          onSubmit={(gebeurtenis) => {
            gebeurtenis.preventDefault();
            if (regel.soort === "klantnummers" || regel.soort === "accounts") {
              const nummers = concept
                .split(/[\s,;]+/)
                .map((deel) => deel.trim())
                .filter(Boolean);
              if (regel.soort === "accounts") {
                onZet(nummers);
                return;
              }
              if (nummers.some((nummer) => !/^\d+$/.test(nummer))) {
                toast.error("Alleen klantnummers (cijfers), gescheiden door een komma.");
                return;
              }
              onZet(nummers);
              return;
            }
            const getal = Number(concept.replace(",", "."));
            if (Number.isNaN(getal)) {
              toast.error("Vul een getal in.");
              return;
            }
            onZet(regel.soort === "getal" ? Math.round(getal) : getal);
          }}
          style={{ display: "flex", gap: 6 }}
        >
          <input
            value={concept}
            onChange={(gebeurtenis) => setConcept(gebeurtenis.target.value)}
            inputMode={
              regel.soort === "klantnummers"
                ? "numeric"
                : regel.soort === "accounts"
                  ? "email"
                  : "decimal"
            }
            aria-label={regel.label}
            style={{
              width:
                regel.soort === "accounts"
                  ? "min(320px, 60vw)"
                  : regel.soort === "klantnummers"
                    ? 200
                    : 90,
              padding: "6px 8px",
              border: "1px solid var(--wp-lijn)",
              borderRadius: 7,
              background: "var(--wp-bg)",
              color: "var(--wp-tekst)",
              /* 16px op mobiel voorkomt dat iOS bij focus inzoomt. */
              fontSize: 16,
            }}
          />
          <button type="submit" className="wp-knop" disabled={bezig}>
            Opslaan
          </button>
        </form>
      )}
    </div>
  );
}
